import { useCallback, useEffect, useRef } from 'react'

import { useRouter } from 'next/compat/router'

import { useApolloClient } from '@apollo/client'

import {
  RuleIsV2Document,
  useRuleIsV2LazyQuery,
} from '@/graphql/generated/hooks'
import { BUTTON_PRESS, mixpanel } from '@/utils/analytics'
import { INCIDENT_RULES } from '@/utils/routes'

import { MixpanelDataIProps } from '../types/types'

interface RuleActionItemI {
  isRuleAction?: boolean
  ruleId?: string | null
}

interface UseRuleActionNavigationIProps {
  mixpanelData?: MixpanelDataIProps
  onClose?: () => void
  // Feed items passed in so the hook can pre-warm the Apollo cache and prefetch
  // page bundles for all rule actions — eliminates the network round-trip on click
  items?: RuleActionItemI[] | null
}

export const useRuleActionNavigation = ({
  mixpanelData,
  onClose,
  items,
}: UseRuleActionNavigationIProps) => {
  const router = useRouter()
  const client = useApolloClient()
  const [fetchRule] = useRuleIsV2LazyQuery()
  // Apollo (3.6) re-fires onCompleted with cached data whenever the calling
  // component re-renders. The ref prevents a second push() from firing while
  // the first navigation is already in progress.
  const navigationFiredRef = useRef(false)

  useEffect(() => {
    const ruleIds = items
      ?.filter((a) => a?.isRuleAction && a?.ruleId)
      .map((a) => a.ruleId as string)
    if (!ruleIds?.length) return
    // Pre-warm Apollo cache and prefetch page bundles so clicking a rule action
    // link navigates instantly instead of waiting on a network round-trip.
    ruleIds.forEach((id) => {
      void client.query({ query: RuleIsV2Document, variables: { id } })
    })
    router.prefetch(`${INCIDENT_RULES}/rule`)
    router.prefetch(`${INCIDENT_RULES}/automation`)
  }, [items, client, router])

  return useCallback(
    (ruleId: string) => {
      mixpanel.track(`${BUTTON_PRESS} Rule Action Link (Feed)`, {
        ...mixpanelData,
        ruleId,
      })
      navigationFiredRef.current = false
      fetchRule({
        variables: { id: ruleId },
        onCompleted: async (data) => {
          if (!data?.rule || navigationFiredRef.current) return
          navigationFiredRef.current = true
          const route = data.rule.isV2 ? 'automation' : 'rule'
          // Sequence onClose after navigation completes via the push() promise so
          // the drawer unmount doesn't race with Next.js loading the page chunk.
          // On abort (hasRouteUpdated === false) or error, reset the guard so a retry works.
          try {
            const hasRouteUpdated = await router.push(
              `${INCIDENT_RULES}/${route}?id=${ruleId}`
            )
            if (hasRouteUpdated) onClose?.()
            else navigationFiredRef.current = false
          } catch {
            navigationFiredRef.current = false
          }
        },
      })
    },
    [fetchRule, mixpanelData, onClose, router]
  )
}
